import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ClipboardList } from "lucide-react";
import { formatDateTime } from "@/lib/format";
import type { AuditLog } from "@db/schema";

const ACTION_COLORS: Record<string, string> = {
  create: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
  update: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  delete: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

function getActionColorClass(action: string): string {
  const key = action.toLowerCase();
  for (const [keyword, cls] of Object.entries(ACTION_COLORS)) {
    if (key.includes(keyword)) return cls;
  }
  return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
}

const columns: ColumnDef<AuditLog>[] = [
  {
    key: "createdAt",
    header: "Date/Time",
    cell: (row) => (
      <span className="text-sm text-muted-foreground whitespace-nowrap">
        {formatDateTime(row.createdAt)}
      </span>
    ),
  },
  {
    key: "actor",
    header: "Actor",
    cell: (row) => (
      <span className="text-sm font-medium">
        {row.actorUserId ? row.actorUserId.slice(0, 8) + "..." : "System"}
      </span>
    ),
  },
  {
    key: "entityType",
    header: "Entity Type",
    cell: (row) => (
      <span className="text-sm capitalize">{row.entityType}</span>
    ),
  },
  {
    key: "entityId",
    header: "Entity ID",
    cell: (row) => (
      <span className="text-sm text-muted-foreground font-mono">
        {row.entityId.length > 12
          ? row.entityId.slice(0, 12) + "..."
          : row.entityId}
      </span>
    ),
  },
  {
    key: "action",
    header: "Action",
    cell: (row) => (
      <Badge
        variant="secondary"
        className={getActionColorClass(row.action)}
      >
        {row.action}
      </Badge>
    ),
  },
];

export default function AuditLogPage() {
  const [search, setSearch] = useState("");
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>("all");

  const { data: auditLogs, isLoading } = useQuery<AuditLog[]>({
    queryKey: ["/api/billing/audit-logs"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  // Derive unique entity types from the data for the filter dropdown
  const entityTypes = useMemo(() => {
    if (!auditLogs) return [];
    const types = new Set(auditLogs.map((log) => log.entityType));
    return Array.from(types).sort();
  }, [auditLogs]);

  const filteredLogs = useMemo(() => {
    if (!auditLogs) return [];
    let result = auditLogs;

    // Apply entity type filter
    if (entityTypeFilter !== "all") {
      result = result.filter((log) => log.entityType === entityTypeFilter);
    }

    // Apply search filter
    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter(
        (log) =>
          log.entityType.toLowerCase().includes(term) ||
          log.entityId.toLowerCase().includes(term) ||
          log.action.toLowerCase().includes(term),
      );
    }

    return result;
  }, [auditLogs, search, entityTypeFilter]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audit Log"
        description="Review all system changes and actions"
      />

      <DataTable
        columns={columns}
        data={filteredLogs}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by entity type, ID, or action..."
        filterSlot={
          <Select value={entityTypeFilter} onValueChange={setEntityTypeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Entity type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {entityTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  <span className="capitalize">{type}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
        emptyIcon={ClipboardList}
        emptyTitle="No audit logs"
        emptyDescription={
          search || entityTypeFilter !== "all"
            ? "No logs match your current filters. Try adjusting your search or filter."
            : "No audit log entries have been recorded yet."
        }
      />
    </div>
  );
}
